import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Card } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { apiErrorMessage, getHttpStatus } from '../../lib/api';
import {
  getLegalObligationInstanceLocation,
  legalDocumentsQueryKeys,
} from '../../lib/legalDocuments';
import { legalOrganizationObligationsPath, legalSiteObligationsPath } from '../../lib/legalDocumentRoutes';

/**
 * 알림 클릭 목적지(`/legal-documents/instances/:instanceId`) — 인스턴스로 층과 목적지를 찾아 **replace** 이동한다: 현장 층이면 그 현장의 의무
 * 목록(그 의무 행 강조), 조직 층(②-g3 G4 · 2026-11-05 — 기관 종사자 직무교육의 임박 알림)이면 「우리 기관의 법정 의무」. 알림이 현장을
 * 싣지 않기 때문에 둔 경유 화면이다(경로 정본 `lib/legalDocumentRoutes.ts`).
 *
 * 404(없음·남의 것·플래그 off — 서버가 가르지 않는다)면 「찾을 수 없음」과 **목록 링크**를 보인다 —
 * 빈 화면이나 `/` 튕김은 막다른 길이다. replace인 이유는 뒤로 가기가 이 경유 화면에 걸려 다시 앞으로
 * 밀리지 않게 하려는 것이다.
 */
export default function InstanceRedirectPage() {
  const { instanceId = '' } = useParams<{ instanceId: string }>();
  const companyId = useAuthStore((s) => s.currentMembership?.companyId) ?? '';
  const { data, error, isLoading } = useQuery({
    queryKey: legalDocumentsQueryKeys.instanceLocation(companyId, instanceId),
    queryFn: () => getLegalObligationInstanceLocation(companyId, instanceId),
    enabled: !!companyId && !!instanceId,
    retry: false,
  });

  if (data) {
    return <Navigate to={data.layer === 'org' ? legalOrganizationObligationsPath() : legalSiteObligationsPath(data.siteId, data.obligationId)} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12" role="status" aria-label="불러오는 중">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
      </div>
    );
  }

  const notFound = getHttpStatus(error) === 404;
  const message = notFound
    ? '이 알림의 법정 서류를 찾을 수 없습니다. 지금 작업 중인 조직에서 볼 수 없는 현장이거나 더 이상 없는 기록입니다.'
    : ((error && apiErrorMessage(error)) ?? '법정 서류를 불러오지 못했습니다.');

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-0">
      <Card padding="none" radius="lg" elevation="md" className="p-6 text-center">
        <p role="alert" className="text-sm font-medium text-ink-900">
          {message}
        </p>
        <Link
          to="/legal-documents"
          className="mt-4 inline-block text-sm font-bold text-brand-600 hover:text-brand-700"
        >
          법정 서류 현장 목록으로
        </Link>
      </Card>
    </div>
  );
}
